import type { Class, ClassAnalysisRefreshResult, ClassAnalytics, Post } from './classType';
import type { UserPublic } from '@/api/user/userType';
import { del, get, post } from '../request';
import { createRequestCoalescer } from '../requestCoalescer.js';

const studentClassListRequest = createRequestCoalescer({ ttlMs: 5000 });
const STUDENT_CLASS_LIST_REQUEST_KEY = 'student-class-list';
const LOCAL_TEACHER_PREVIEW_KEY = 'probfun_local_teacher_preview';
const LOCAL_STUDENT_PREVIEW_KEY = 'probfun_local_student_preview';

function isLocalTeacherPreview() {
  return import.meta.env.DEV && Boolean(sessionStorage.getItem(LOCAL_TEACHER_PREVIEW_KEY));
}

function isLocalStudentPreview() {
  return import.meta.env.DEV && Boolean(sessionStorage.getItem(LOCAL_STUDENT_PREVIEW_KEY));
}

function clearStudentClassListRequestCache() {
  studentClassListRequest.clear(STUDENT_CLASS_LIST_REQUEST_KEY);
}

async function postPostApi(title: string, content: string, teachingClassIds: string[]) {
  const result = await post<{
    post: Post;
  }>(`/class/post/`, {
    title,
    content,
    teachingClassIds,
  });
  return result.data;
}

async function fetchPostApi(classId: string | number | Array<string | number>) {
  const teachingClassIds = Array.isArray(classId) ? classId : [classId];
  const result = await get<{
    posts: Post[];
  }>(`/class/post/list/`, {
    teachingClassIds,
  });
  return result.data;
}

async function readPostApi(postIds: number[]) {
  const result = await post<null>(`/class/post/read/`, {
    postIds,
  });
  return result.data;
}

async function fetchStudentClassListApi(options?: { force?: boolean }) {
  if (isLocalStudentPreview()) {
    return {
      teachingClasses: [{
        id: '__local_test_class__',
        name: '本地个人学情预览班（模拟数据）',
        code: 'STUDENT-PREVIEW',
        createdAt: new Date().toISOString(),
        subjectId: null,
        subjectName: null,
      }],
    };
  }

  if (options?.force) {
    clearStudentClassListRequestCache();
  }

  return studentClassListRequest.run(STUDENT_CLASS_LIST_REQUEST_KEY, async () => {
    const result = await get<{
      teachingClasses: Class[];
    }>(`/class/student/teaching-class/list/`);
    return result.data;
  });
}

async function joinClassApi(code: string) {
  const result = await post<null>(`/class/student/join/`, {
    code,
  });
  clearStudentClassListRequestCache();
  return result.data;
}

async function quitClassApi(classId: string) {
  const result = await post<null>(`/class/student/exit/`, {
    classId,
  });
  clearStudentClassListRequestCache();
  return result.data;
}

async function createClassApi(name: string, subjectId?: string | number) {
  if (isLocalTeacherPreview()) {
    return {
      teaching_class_id: `preview-class-${Date.now()}`,
      name,
      code: 'LOCAL01',
      createdAt: new Date().toISOString(),
      subjectId: subjectId ?? null,
      subjectName: subjectId ? '本地预览课程' : null,
    };
  }

  const result = await post<{
    teaching_class_id: string | number;
    name: string;
    code: string;
    createdAt: string;
    subjectId: string | number | null;
    subjectName: string | null;
  }>(`/class/teacher/create/`, {
    name,
    ...(subjectId === undefined || subjectId === null || String(subjectId).trim() === ''
      ? {}
      : { subjectId: String(subjectId) }),
  });
  return result.data;
}

async function fetchTeacherClassListApi(subjectId?: string | number) {
  if (isLocalTeacherPreview()) {
    const now = new Date().toISOString();
    return {
      teachingClasses: [
        { id: 'preview-class-1', name: '2026 春 概率论 1 班', code: 'P2026A', createdAt: now, subjectId: 9001, subjectName: '概率论教学示例' },
        { id: 'preview-class-2', name: '2026 春 概率论 2 班', code: 'P2026B', createdAt: now, subjectId: null, subjectName: null },
        { id: 'preview-class-3', name: '2026 春 线性代数 1 班', code: 'L2026A', createdAt: now, subjectId: 9002, subjectName: '线性代数发布示例' },
      ],
    };
  }

  const result = await get<{
    teachingClasses: Class[];
  }>(
    `/class/teacher/teaching-class/list/`,
    subjectId === undefined || subjectId === null || String(subjectId).trim() === ''
      ? {}
      : { subjectId: String(subjectId) },
  );

  return result.data;
}

async function deleteClassApi(classId: string) {
  const result = await del<null>(`/class/teacher/delete/${classId}/`, {
    classId,
  });
  return result.data;
}

async function fetchStudentListApi(classId: string) {
  const result = await get<{
    students: UserPublic[];
  }>(`/class/teacher/student/list/`, {
    classId,
  });
  return result.data;
}

async function fetchClassAnalyticsApi(
  teachingClassId: string,
  subjectId: string | number,
  config?: any,
) {
  // 确保 subjectId 是字符串格式（后端要求）
  const result = await get<ClassAnalytics>(`/assessment/class/${teachingClassId}/analysis/`, {
    subjectId: String(subjectId),
  }, config);
  return result.data;
}

async function fetchTeachingClassQuestionAccuracyApi(teachingClassId: string, subjectId: string | number, chapterId?: string | number) {
  const params: Record<string, string> = {
    subjectId: String(subjectId),
  };
  if (chapterId !== undefined && chapterId !== null && chapterId !== '')
    params.chapterId = String(chapterId);

  const result = await get<{
    teachingClassId: string | number;
    subjectId: string | number;
    chapterId: string | number | null;
    totalStudents: number;
    questions: Array<{
      questionId: string | number;
      firstAttemptAccuracy: number | null;
      firstAttemptStudentCount: number;
      firstAttemptCorrectCount: number;
      totalStudentCount: number;
      hasAttemptData: boolean;
    }>;
  }>(`/assessment/class/${teachingClassId}/question-accuracy/`, params);
  return result.data;
}

/** 清除指定班级+学科的分析缓存，下次请求将重新计算最新数据 */
async function refreshClassAnalysisCacheApi(
  teachingClassId: string,
  subjectId: string | number,
  config?: any,
) {
  const result = await del<ClassAnalysisRefreshResult>(
    `/assessment/class/${teachingClassId}/analysis/refresh/`,
    { subjectId: String(subjectId) },
    config,
  );
  return result.data;
}

async function removeStudentApi(teachingClassId: string, studentId: string) {
  const result = await del<null>(`/class/teacher/student/remove/`, {
    teachingClassId,
    studentId,
  });
  return result.data;
}

export {
  createClassApi,
  deleteClassApi,
  fetchClassAnalyticsApi,
  fetchPostApi,
  fetchStudentClassListApi,
  fetchStudentListApi,
  fetchTeacherClassListApi,
  fetchTeachingClassQuestionAccuracyApi,
  joinClassApi,
  postPostApi,
  quitClassApi,
  readPostApi,
  refreshClassAnalysisCacheApi,
  removeStudentApi,
};
